export interface Point { x: number; y: number }
export type Direction = 'left' | 'right' | 'down'
export type GestureMode = Direction | 'hold'
export interface GestureOptions {
  directions: Direction[]
  threshold?: number
  thresholds?: Partial<Record<Direction, number>>
  hold?: () => void
  onMove: (mode: GestureMode, point: Point, distance: number) => void
  onCommit: (mode: GestureMode) => void
  onReset: () => void
  onActivate: () => void
}
export const HOLD_MS = 450
export const SWIPE_DISTANCE = 72
export const DISMISS_DISTANCE = 120

/** One direction owns a sequence. Ambiguous/vertical movement yields permanently to scrolling. */
export function createGesture(origin: Point, options: GestureOptions) {
  let mode: GestureMode | null = null
  let finished = false
  let distance = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  const clear = () => { if (timer !== undefined) clearTimeout(timer); timer = undefined }
  const cancel = () => { if (finished) return; finished = true; clear(); options.onReset() }
  if (options.hold) timer = setTimeout(() => {
    if (finished) return
    mode = 'hold'
    options.onActivate()
    options.hold!()
  }, HOLD_MS)
  return {
    move(point: Point) {
      if (finished) return false
      const dx = point.x - origin.x, dy = point.y - origin.y
      if (Math.hypot(dx, dy) > 8) clear()
      if (!mode) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 12) return false
        const direction = Math.abs(dx) > Math.abs(dy) * 1.4 ? (dx < 0 ? 'left' : 'right')
          : dy > Math.abs(dx) * 1.4 ? 'down' : null
        if (!direction || !options.directions.includes(direction)) { cancel(); return false }
        mode = direction
        options.onActivate()
      }
      distance = mode === 'left' ? -dx : mode === 'right' ? dx : dy
      options.onMove(mode, point, Math.max(0, distance))
      return true
    },
    release() {
      if (finished) return
      const commit = mode && (mode === 'hold' || distance >= (options.thresholds?.[mode] ?? options.threshold ?? SWIPE_DISTANCE))
      finished = true
      clear()
      // Hold consumers must drop before reset cancels their reorder session.
      if (commit) options.onCommit(mode!)
      options.onReset()
    },
    cancel,
    get active() { return !finished && mode !== null },
  }
}
